import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import {
  ArrowLeft,
  ArrowRight,
  Check,
  Printer,
  Reply,
  Save,
  Trash2,
  TriangleAlert,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { qualityMachine } from '@/config/qualityStates';
import { useQualityDetailContext } from '@/contexts/QualityContext';
import { useConfirmModal } from '@/hooks/useConfirmModal';
import { DETAIL_MODE, DETAIL_TYPE, useDetailMode } from '@/hooks/useDetailMode';
import useQualityActions from '@/hooks/useQualityActions';
import { getUserInfo } from '@/lib/auth';

const QUALITY_STEPS = ['information', 'nonconformity', 'plan', 'approvals'];

export const QualityDetailActionsHead = () => {
  const navigate = useNavigate();
  const { mode } = useDetailMode(DETAIL_TYPE.QUALITY);
  const { form } = useQualityDetailContext();

  const actions = useQualityActions();

  const { showConfirmModal } = useConfirmModal();

  const handleDraftClick = () => {
    showConfirmModal({
      title: 'Save Draft',
      description:
        'Are you sure you want to save your changes as a draft and go back to the list?',
      onConfirm:
        mode === DETAIL_MODE.EDIT ? actions.saveDraft : actions.createDraft,
    });
  };

  const handleDeleteClick = () => {
    showConfirmModal({
      title: 'Delete Draft',
      description:
        'This action permanently deletes this draft and cannot be undone. Are you sure?',
      onConfirm: actions.deleteDraft,
      variant: 'destructive',
    });
  };

  return (
    <>
      <Button
        onClick={() =>
          navigate('/qualities', {
            state: mode === DETAIL_MODE.EDIT ? 'pending' : 'master',
          })
        }
        variant="outline"
      >
        <Reply className="h-4 w-4 mr-2" />
        {mode === DETAIL_MODE.EDIT
          ? 'Back to Pending List'
          : 'Back to Master List'}
      </Button>
      {(mode === DETAIL_MODE.NEW ||
        (mode === DETAIL_MODE.EDIT && form.status === 'DRAFT')) && (
        <Button onClick={handleDraftClick} variant="outline">
          <Save className="h-4 w-4 mr-2" />
          Save Draft
        </Button>
      )}
      {mode === DETAIL_MODE.EDIT && form.status === 'DRAFT' && (
        <Button
          className="border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive"
          onClick={handleDeleteClick}
          variant="outline"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Delete Draft
        </Button>
      )}
    </>
  );
};

export const QualityDetailActionsTail = ({
  allowedActions: allowedActionsProp,
  blockingCommentsCount = 0,
  canApprove = true,
  reviews = [],
}) => {
  const { mode } = useDetailMode(DETAIL_TYPE.QUALITY);
  const { activeTab, form, setActiveTab } = useQualityDetailContext();

  const actions = useQualityActions();

  const { showConfirmModal } = useConfirmModal();
  const allowedActions = useMemo(() => {
    if (allowedActionsProp) return allowedActionsProp;

    const userInfo = getUserInfo();
    const reviewerPipeline =
      form.reviewerPipeline || qualityMachine.buildPipeline(form.reviewers);

    return form.status
      ? qualityMachine.getActions(
          form.status,
          userInfo?.sub,
          reviewerPipeline,
          form.currentActorIndex ?? 0,
          form.creator || form.requestorId,
          userInfo?.['cognito:groups']
        )
      : [];
  }, [
    allowedActionsProp,
    form.creator,
    form.currentActorIndex,
    form.requestorId,
    form.reviewerPipeline,
    form.reviewers,
    form.status,
  ]);

  const handlePreviousClick = () => {
    const current = QUALITY_STEPS.indexOf(activeTab);

    setActiveTab(QUALITY_STEPS[current - 1]);
  };

  const handleNextClick = () => {
    const current = QUALITY_STEPS.indexOf(activeTab);

    setActiveTab(QUALITY_STEPS[current + 1]);
  };

  const handleSubmitClick = () => {
    if (mode === DETAIL_MODE.NEW)
      showConfirmModal({
        title: 'Submit Quality Event',
        description:
          'Are you sure you want to submit this form? This action cannot be undone.',
        onConfirm: actions.createQuality,
      });
    else
      showConfirmModal({
        title: 'Submit Quality Event',
        description:
          form.status === 'RETURNED'
            ? 'Are you sure you want to resubmit this for review? This action cannot be undone.'
            : 'Are you sure you want to submit this draft? This action cannot be undone.',
        onConfirm: actions.submitDraft,
      });
  };

  const [rejectReason, setRejectReason] = useState('');
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);

  const handleRejectClick = () => {
    setRejectReason('');
    setIsRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    await actions.reject(rejectReason);
    setIsRejectDialogOpen(false);
    setRejectReason('');
  };

  const handleReturnClick = () =>
    showConfirmModal({
      title: 'Return to Requestor',
      description:
        'Are you sure you want to return this quality event to the requestor with the current comments?',
      onConfirm: actions.returnToRequestor,
    });

  const handleApproveClick = () =>
    showConfirmModal({
      title: 'Approve Quality Event',
      description:
        'Are you sure you want to approve this quality event? This action cannot be undone.',
      onConfirm: actions.approve,
    });

  const isFirstTab = activeTab === QUALITY_STEPS[0];
  const isLastTab = activeTab === QUALITY_STEPS[QUALITY_STEPS.length - 1];

  return (
    <>
      {mode === DETAIL_MODE.STATIC && (
        <div className="flex items-center space-x-2">
          {allowedActions.includes('approve') && blockingCommentsCount > 0 && (
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-amber-50 text-xs text-amber-700 whitespace-nowrap">
              <TriangleAlert className="shrink-0 h-3.5 w-3.5" />
              <span>
                {blockingCommentsCount} unresolved comment
                {blockingCommentsCount > 1 ? 's' : ''}
              </span>
            </div>
          )}
          {allowedActions.includes('reject') && (
            <Button
              className="align-top bg-red-500"
              onClick={handleRejectClick}
            >
              REJECT
            </Button>
          )}
          {allowedActions.includes('returnToRequestor') && (
            <Button
              className="align-top bg-red-500"
              disabled={reviews.length === 0}
              onClick={handleReturnClick}
            >
              RETURN TO REQUESTOR
            </Button>
          )}
          {allowedActions.includes('approve') && (
            <Button
              className="align-top"
              disabled={!canApprove || blockingCommentsCount > 0}
              onClick={handleApproveClick}
            >
              APPROVE
            </Button>
          )}
          {allowedActions.includes('newRevision') && actions.newRevision && (
            <Button
              className="align-top"
              onClick={actions.newRevision}
              variant="outline"
            >
              NEW REVISION
            </Button>
          )}
        </div>
      )}
      {mode === DETAIL_MODE.NEW && !isFirstTab && (
        <Button
          className="align-top"
          onClick={handlePreviousClick}
          variant="outline"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Previous
        </Button>
      )}
      {mode === DETAIL_MODE.NEW && !isLastTab && (
        <Button
          className="align-top"
          onClick={handleNextClick}
          variant="outline"
        >
          Next
          <ArrowRight className="h-4 w-4 mr-2" />
        </Button>
      )}
      {((mode === DETAIL_MODE.NEW && isLastTab) || mode === DETAIL_MODE.EDIT) &&
        mode !== DETAIL_MODE.STATIC && (
          <Button disabled={actions.isSubmitting} onClick={handleSubmitClick}>
            {form.status === 'RETURNED' ? 'SUBMIT FOR REVIEW' : 'Submit'}
            <Check className="h-4 w-4 mr-2" />
          </Button>
        )}

      {/* ── Reject Dialog ───────────────────── */}
      <Dialog
        open={isRejectDialogOpen}
        onOpenChange={(open) => {
          setIsRejectDialogOpen(open);
          if (!open) setRejectReason('');
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Reject Quality Event</DialogTitle>
            <DialogDescription>
              You are about to reject this quality event. Please provide a
              reason below.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label className="text-sm font-medium mb-1 block">
              Reason for Rejection
            </label>
            <Textarea
              className="font-mono resize-none"
              onChange={({ target }) => setRejectReason(target.value)}
              placeholder="Enter reason for rejection..."
              rows={4}
              value={rejectReason}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsRejectDialogOpen(false);
                setRejectReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              className="bg-red-500"
              disabled={!rejectReason.trim()}
              onClick={handleRejectConfirm}
            >
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const QualityProgressionActionsHead = () => {
  const navigate = useNavigate();

  return (
    <>
      <Button onClick={() => navigate('/qualities')} variant="outline">
        <Reply />
        Back to Master List
      </Button>
    </>
  );
};

export const QualityProgressionActionsTail = () => {
  const handlePrintClick = () => window.print();

  return (
    <>
      <Button onClick={handlePrintClick} variant="outline">
        <Printer />
        Print
      </Button>
    </>
  );
};
